/**
 * Turn a finished Ruta AI stream into the bubble a person sees.
 *
 * The chat used to fall back to the literal "No response" when the stream
 * ended with no text — including when the server sent an `error` event the
 * client ignored. Help-write then offered "Use in message" on that empty
 * shell (RUT-3874).
 */

import {
  ASSISTANT_SAFE_ERROR,
  finishAssistantTurnOnStreamClose,
  looksLikeRawTechnicalError,
  type AssistantTurnView,
} from '@ruta/shared';

export const ASSISTANT_EMPTY_REPLY = 'Ruta AI did not return a reply. Try again.';

const CREDIT_EXHAUSTED =
  /credit|quota|insufficient|exhaust|allowance/i;

/** Map a raw stream / HTTP error into something an operator can act on. */
export function assistantErrorCopy(raw: string | undefined | null): string {
  const msg = (raw ?? '').trim();
  if (!msg) return ASSISTANT_SAFE_ERROR;
  if (CREDIT_EXHAUSTED.test(msg)) {
    return 'This company is out of Ruta AI credits for this period.';
  }
  if (looksLikeRawTechnicalError(msg)) return ASSISTANT_SAFE_ERROR;
  return msg;
}

export function finalizeAssistantTurn(params: {
  assistantText: string;
  streamError?: string | null;
}): { content: string; failed: boolean } {
  const text = params.assistantText.trim();
  if (text) {
    return {
      content: looksLikeRawTechnicalError(text) ? ASSISTANT_SAFE_ERROR : text,
      failed: looksLikeRawTechnicalError(text),
    };
  }
  if (params.streamError) {
    return { content: assistantErrorCopy(params.streamError), failed: true };
  }
  return { content: ASSISTANT_EMPTY_REPLY, failed: true };
}

/** Stream closed without `done` — unlock the dock and clear the status line. */
export function closeAssistantStream(view: AssistantTurnView): AssistantTurnView {
  return finishAssistantTurnOnStreamClose(view);
}
